import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toasterMsg } from '../../../../../Components/Toaster/Toaster';
import { uploadDesignImageToS3, saveDesignImageDetail } from '../../../store/DevelopmentOrder.action';
import { useBulkEditState } from '../BulkEditProvider';
import { hasStatusComment } from '../../../DesignDetail/utils/deriveDesignStatus';
import { applyBulkPdfExcludedComment, applyBulkStatus, fetchBulkDesignRows, validateDesignProducts } from './design-details-bulk.service';
import DesignBulkAgGrid from './DesignBulkAgGrid';
import Loader from '../../../../../Components/Utility/Loader/Loader';
import { BombiixApprovalBar } from '../../../../../Components/BombiixApprovalBar';
import type { ApprovalOption } from '../../../../../Components/BombiixApprovalBar';
import CanvasCommentPopup from './CanvasCommentPopup';
import ProductExclusionModal from '../ProductExclusionModal';
import type {
  BulkAgRow,
  BulkGridCanvas,
  BulkGridHandlers,
  BulkGridRow,
  BulkSortOrder,
  BulkStatusItem,
  DesignStatusValue,
  IneligibleDesignProduct,
} from '../types';
import './design-details-bulk-grid.css';

/** Compose a stable selection key for one canvas cell. */
const cellKey = (devOrderId: string, designId: string): string => `${devOrderId}::${designId}`;

/** Floor for the measured grid height, so the grid stays usable on short viewports. */
const MIN_GRID_HEIGHT = 240;

/** Products fetched per scroll page. Must not exceed the endpoint's MAX_PAGE_LIMIT. */
const PAGE_SIZE = 50;

/** Debounce for the header search before it becomes a server round-trip. */
const SEARCH_DEBOUNCE_MS = 300;

interface CommentTarget {
  devOrderId: string;
  canvas: BulkGridCanvas;
  /** The comment icon itself, so the popover can stay tethered to its cell while the grid scrolls. */
  anchor: HTMLElement;
}

/**
 * Design Details bulk grid — the first fully-built Bulk Edit module. Loads the
 * products × canvases matrix for the selected products (rendered with the shared
 * BombiixAGGrid), supports Select-All / row / cell approval selection, per-canvas
 * rich comments (with push-to-all), and a bottom approve/reject bar that mirrors
 * the single-DO design-details workflow. Save / Reset / Download live in the grid
 * floating bar and apply one status/comment/date/supplier across the selection.
 * Supplier + search are owned by the page header via the Bulk Edit context.
 */
const DesignDetailsBulkGrid: React.FC = () => {
  const { t: _t } = useTranslation('bulk-edit');
  /** TranslateFn declared globally in src/global.d.ts */
  const t = _t as unknown as TranslateFn;
  const { t: tCommon } = useTranslation('common');
  const { selectedProductIds, designSearchText } = useBulkEditState();
  const history = useHistory();
  const reduxDispatch = useDispatch();
  /** Redux thunks resolve to a promise; the base Dispatch type doesn't model that. */
  const runThunk = reduxDispatch as unknown as (_action: unknown) => Promise<{ success?: boolean; s3Key?: string } | undefined>;

  const [rows, setRows] = useState<BulkGridRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  /**
   * The selected products that actually carry design details, resolved once per selection by
   * the validate call. `null` means "not validated yet" — the grid must not fetch a page
   * before it knows the eligible cohort, or excluded products would flash into the first page.
   */
  const [eligibleIds, setEligibleIds] = useState<string[] | null>(null);
  const [excludedProducts, setExcludedProducts] = useState<IneligibleDesignProduct[]>([]);
  const [showExclusionModal, setShowExclusionModal] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  /** True while a comment save or slot upload is in flight — drives the busy overlay. */
  const [mutating, setMutating] = useState<boolean>(false);
  const [sortOrder, setSortOrder] = useState<BulkSortOrder>('asc');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  /** Guards against an earlier page resolving after a newer sort/search reset. */
  const requestIdRef = useRef<number>(0);

  /** Every loaded cell key, synced each render below — read by loadPage's selection updaters. */
  const allCellKeysRef = useRef<string[]>([]);

  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  /** Unsaved per-canvas pdfExcludedComment edits from the popup (cellKey → comment), for immediate grid display. */
  const [perCanvasPdfComments, setPerCanvasPdfComments] = useState<Record<string, string>>({});
  const [statusIntent, setStatusIntent] = useState<DesignStatusValue | null>(null);
  const [commentDate, setCommentDate] = useState<Date | null>(new Date());
  const [footerComment, setFooterComment] = useState<string>('');
  /** Marks the footer comment invalid after a Save attempt with a missing mandatory comment — not while typing. */
  const [commentError, setCommentError] = useState<boolean>(false);
  const [commentTarget, setCommentTarget] = useState<CommentTarget | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number | null>(null);

  /**
   * The page shell above this grid is not height-constrained, so a percentage
   * height has nothing to resolve against. Measure the distance from the grid's
   * own top to the viewport bottom instead — this keeps the grid filling the
   * page without hard-coding an offset for the headers above it.
   */
  useLayoutEffect(() => {
    const measure = (): void => {
      const element = containerRef.current;
      if (!element) {
        return;
      }
      const { top } = element.getBoundingClientRect();
      setContainerHeight(Math.max(MIN_GRID_HEIGHT, Math.round(window.innerHeight - top)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [selectedProductIds]);

  /** Debounce the header search so each keystroke is not a request. */
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(designSearchText.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [designSearchText]);

  /**
   * Resolve the eligible cohort once per selection, before the first page loads. Products
   * with no design details have nothing to approve, reject or comment on, so they are
   * dropped from the grid entirely and named in a popup. Validating the whole selection
   * server-side (rather than filtering each loaded page) keeps the exclusion consistent
   * across paging, sort, search and the post-save refresh, and keeps `total` truthful.
   */
  useEffect(() => {
    if (selectedProductIds.length === 0) {
      setEligibleIds([]);
      setExcludedProducts([]);
      setShowExclusionModal(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const result = await validateDesignProducts(selectedProductIds);
        if (cancelled) {
          return;
        }
        setEligibleIds(result.eligibleProductIds);
        setExcludedProducts(result.ineligibleProducts);
        setShowExclusionModal(result.ineligibleProducts.length > 0);
      } catch (error) {
        if (cancelled) {
          return;
        }
        /* Validation is a filter, not a gate: if it fails, fall back to the full selection so
           the user still gets a grid rather than an empty screen. */
        setEligibleIds(selectedProductIds);
        setExcludedProducts([]);
        setShowExclusionModal(false);
        toasterMsg(t('LOAD_FAILED'), 'danger');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedProductIds, t]);

  /**
   * Fetch one page. `skip === 0` replaces the grid (initial load, sort change,
   * search change, post-save refresh); anything else appends the next page.
   * Every fetch is scoped to `eligibleIds`, never the raw selection, so an excluded
   * product cannot re-enter the grid through a later page, a re-sort or a refresh.
   */
  const loadPage = useCallback(
    async (skip: number): Promise<void> => {
      /* Not validated yet — the effect below re-runs this once the cohort resolves. */
      if (eligibleIds === null) {
        return;
      }
      if (eligibleIds.length === 0) {
        setRows([]);
        setTotal(0);
        return;
      }
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      if (skip === 0) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      try {
        const page = await fetchBulkDesignRows({
          devOrderIds: eligibleIds,
          skip,
          limit: PAGE_SIZE,
          sortOrder,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        });
        /* A newer request superseded this one — its rows are already stale. */
        if (requestIdRef.current !== requestId) {
          return;
        }
        setTotal(page.total);
        setRows((prev) => (skip === 0 ? page.rows : [...prev, ...page.rows]));

        const pageKeys = page.rows.flatMap((row) => row.canvases.map((canvas) => cellKey(row.devOrderId, canvas.designId)));
        if (skip === 0) {
          /* Rows were replaced (search / sort / selection change / refresh): prune the
             selection to the canvases still visible, so Save can never silently apply a
             status to rows the user no longer sees and the header checkbox state stays
             truthful. */
          const visibleKeys = new Set(pageKeys);
          setSelectedCells((prev) => {
            const next = new Set([...prev].filter((key) => visibleKeys.has(key)));
            return next.size === prev.size ? prev : next;
          });
        } else {
          /* Rows were appended (infinite scroll): when everything loaded so far was
             selected (Select All), the new page joins the selection — otherwise the
             header would silently drop from checked to indeterminate on scroll.
             The pre-append keys are captured here, before this batch re-renders. */
          const loadedKeys = allCellKeysRef.current;
          setSelectedCells((prev) => {
            const wasAllSelected = loadedKeys.length > 0 && loadedKeys.every((key) => prev.has(key));
            if (!wasAllSelected) {
              return prev;
            }
            const next = new Set(prev);
            pageKeys.forEach((key) => next.add(key));
            return next;
          });
        }
      } catch (error) {
        if (requestIdRef.current === requestId) {
          toasterMsg(t('LOAD_FAILED'), 'danger');
        }
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [eligibleIds, sortOrder, debouncedSearch, t],
  );

  /** Reset to the first page whenever the eligible cohort, sort or search changes. */
  useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  const hasMore = rows.length < total;

  const onLoadMore = useCallback((): void => {
    if (loading || loadingMore || !hasMore) {
      return;
    }
    loadPage(rows.length);
  }, [loading, loadingMore, hasMore, loadPage, rows.length]);

  const allCellKeys = useMemo(() => rows.flatMap((row) => row.canvases.map((canvas) => cellKey(row.devOrderId, canvas.designId))), [rows]);
  const columnCount = useMemo(() => rows.reduce((max, row) => Math.max(max, row.canvases.length), 0), [rows]);
  const allSelected = allCellKeys.length > 0 && allCellKeys.every((key) => selectedCells.has(key));
  const someSelected = selectedCells.size > 0;

  /** Fold selection + comment state into each row. Search/sort are applied server-side. */
  const agRows = useMemo<BulkAgRow[]>(
    () =>
      rows.map((row) => {
        const selectedCanvasIds = row.canvases.filter((canvas) => selectedCells.has(cellKey(row.devOrderId, canvas.designId))).map((canvas) => canvas.designId);
        const canvasComments: Record<string, string> = {};
        row.canvases.forEach((canvas) => {
          const override = perCanvasPdfComments[cellKey(row.devOrderId, canvas.designId)];
          if (override !== undefined) {
            canvasComments[canvas.designId] = override;
          }
        });
        return {
          devOrderId: row.devOrderId,
          productNumber: row.productNumber,
          productName: row.productName,
          canvases: row.canvases,
          selectedCanvasIds,
          canvasComments,
          rowSelected: row.canvases.length > 0 && selectedCanvasIds.length === row.canvases.length,
        };
      }),
    [rows, selectedCells, perCanvasPdfComments],
  );

  /**
   * The toggle handlers are passed to every cell through the column definitions,
   * so they must keep a stable identity: closing over `rows`/`selectedCells` gave
   * them a new identity on every click, which rebuilt every column and re-rendered
   * every cell just to tick one checkbox. They read current state from refs and
   * decide the next selection inside the updater instead.
   */
  const rowsRef = useRef<BulkGridRow[]>(rows);
  rowsRef.current = rows;
  allCellKeysRef.current = allCellKeys;

  const onToggleAll = useCallback((): void => {
    const keys = allCellKeysRef.current;
    setSelectedCells((prev) => {
      const fullySelected = keys.length > 0 && keys.every((key) => prev.has(key));
      const next = new Set(prev);
      keys.forEach((key) => (fullySelected ? next.delete(key) : next.add(key)));
      return next;
    });
  }, []);

  const onToggleRow = useCallback((devOrderId: string): void => {
    const row = rowsRef.current.find((entry) => entry.devOrderId === devOrderId);
    if (!row) {
      return;
    }
    const keys = row.canvases.map((canvas) => cellKey(devOrderId, canvas.designId));
    setSelectedCells((prev) => {
      const fullySelected = keys.length > 0 && keys.every((key) => prev.has(key));
      const next = new Set(prev);
      keys.forEach((key) => (fullySelected ? next.delete(key) : next.add(key)));
      return next;
    });
  }, []);

  const onToggleCanvas = useCallback((devOrderId: string, designId: string): void => {
    const key = cellKey(devOrderId, designId);
    setSelectedCells((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const onEditComment = useCallback((devOrderId: string, canvas: BulkGridCanvas, anchor: HTMLElement): void => setCommentTarget({ devOrderId, canvas, anchor }), []);

  const onDownloadRow = useCallback(() => toasterMsg(t('DOWNLOAD_NOT_AVAILABLE'), 'info'), [t]);

  const onOpenProduct = useCallback((devOrderId: string): void => {
    window.open(`/development-order/${devOrderId}/design-details/List`, '_blank', 'noopener,noreferrer');
  }, []);

  const onUploadSlot = useCallback(
    async (devOrderId: string, canvasIndex: number, designId: string, slotIndex: number, file: File): Promise<void> => {
      setMutating(true);
      try {
        const uploadResult = await runThunk(uploadDesignImageToS3(file, devOrderId, canvasIndex, slotIndex, () => undefined));
        if (!uploadResult?.success || !uploadResult?.s3Key) {
          toasterMsg(t('IMAGE_UPLOAD_FAILED'), 'danger');
          return;
        }
        await runThunk(
          saveDesignImageDetail(devOrderId, designId, { imageId: null, imageIndex: slotIndex, s3Key: uploadResult.s3Key, imageTitle: '', imageDescription: '' }, tCommon),
        );
        toasterMsg(t('IMAGE_UPLOADED'), 'success');
        await loadPage(0);
      } catch (error) {
        toasterMsg(t('IMAGE_UPLOAD_FAILED'), 'danger');
      } finally {
        setMutating(false);
      }
    },
    [runThunk, t, tCommon, loadPage],
  );

  const handlers = useMemo<BulkGridHandlers>(
    () => ({ onToggleCanvas, onToggleRow, onEditComment, onDownloadRow, onUploadSlot, onOpenProduct }),
    [onToggleCanvas, onToggleRow, onEditComment, onDownloadRow, onUploadSlot, onOpenProduct],
  );

  /**
   * Persist a per-canvas PDF-excluded comment as soon as the popup closes (cross or
   * outside click). This writes only the design annotation (`pdfExcludedComment`) via
   * the bulk pdf-excluded-comment endpoint — the approval status/comment/date are left
   * untouched. "Push to all" applies the comment to every selected canvas; status is
   * irrelevant, so the targets are simply grouped by dev order.
   */
  const onSaveComment = useCallback(
    async (comment: string, pushToAll: boolean): Promise<void> => {
      if (!commentTarget) {
        return;
      }
      const targetKey = cellKey(commentTarget.devOrderId, commentTarget.canvas.designId);
      setCommentTarget(null);

      /** Reflect the comment in the grid immediately. */
      setPerCanvasPdfComments((prev) => {
        const next = { ...prev, [targetKey]: comment };
        if (pushToAll) {
          selectedCells.forEach((key) => {
            next[key] = comment;
          });
        }
        return next;
      });

      const cellKeys = pushToAll && selectedCells.size > 0 ? [...selectedCells] : [targetKey];
      const byDevOrder = new Map<string, string[]>();
      cellKeys.forEach((key) => {
        const [devOrderId, designId] = key.split('::');
        if (!devOrderId || !designId) {
          return;
        }
        const designIds = byDevOrder.get(devOrderId) ?? [];
        designIds.push(designId);
        byDevOrder.set(devOrderId, designIds);
      });

      if (byDevOrder.size === 0) {
        return;
      }

      setMutating(true);
      try {
        const items: BulkStatusItem[] = [...byDevOrder].map(([devOrderId, designIds]) => ({ devOrderId, designIds }));
        await applyBulkPdfExcludedComment({ items, comment });
        toasterMsg(t('BULK_COMMENT_APPLIED'), 'success');
        await loadPage(0);
      } catch (error) {
        toasterMsg(t('BULK_COMMENT_FAILED'), 'danger');
      } finally {
        setMutating(false);
      }
    },
    [commentTarget, selectedCells, loadPage, t],
  );

  const resetSelection = useCallback((): void => {
    setSelectedCells(new Set());
    setPerCanvasPdfComments({});
    setStatusIntent(null);
    setFooterComment('');
    setCommentDate(new Date());
    setCommentError(false);
  }, []);

  /** Switching status clears any stale comment error from a prior Save attempt. */
  const onStatusChange = useCallback((value: DesignStatusValue): void => {
    setStatusIntent(value);
    setCommentError(false);
  }, []);

  /** Typing a meaningful comment clears the invalid state raised by Save. */
  const onFooterCommentChange = useCallback((value: string): void => {
    setFooterComment(value);
    if (hasStatusComment(value)) {
      setCommentError(false);
    }
  }, []);

  /**
   * Resolve the status comment to persist. Every status carries the footer comment,
   * including plain Approve — the bar accepts a comment for all of them, and the
   * single-product approve/reject flow saves it the same way. The per-canvas popup
   * edits `pdfExcludedComment`, a separate field, so it does not feed this value.
   * @returns the footer comment, or '' when none was entered
   */
  const statusCommentForSave = useCallback((): string => footerComment ?? '', [footerComment]);

  const onSave = useCallback(async (): Promise<void> => {
    const selected = [...selectedCells];
    if (selected.length === 0 || statusIntent === null || commentDate === null) {
      toasterMsg(t('SELECT_STATUS_AND_ROWS'), 'danger');
      return;
    }
    const statusComment = statusCommentForSave();
    if (statusIntent === 'approved_with_comments' && !hasStatusComment(statusComment)) {
      setCommentError(true);
      toasterMsg(t('COMMENT_REQUIRED_FOR_APPROVE'), 'danger');
      return;
    }

    const status = statusIntent;
    const groupedByComment = new Map<string, Map<string, Set<string>>>();
    selected.forEach((key) => {
      const [devOrderId, designId] = key.split('::');
      if (!devOrderId || !designId) {
        return;
      }
      const comment = statusComment;
      const byDevOrder = groupedByComment.get(comment) ?? new Map<string, Set<string>>();
      const designIds = byDevOrder.get(devOrderId) ?? new Set<string>();
      designIds.add(designId);
      byDevOrder.set(devOrderId, designIds);
      groupedByComment.set(comment, byDevOrder);
    });

    setSaving(true);
    try {
      for (const [comment, byDevOrder] of groupedByComment) {
        const items: BulkStatusItem[] = [...byDevOrder].map(([devOrderId, designIds]) => ({ devOrderId, designIds: [...designIds] }));
        await applyBulkStatus({
          items,
          status,
          statusComment: comment,
          commentDate: commentDate.toISOString(),
        });
      }
      toasterMsg(t('BULK_STATUS_APPLIED'), 'success');
      resetSelection();
      await loadPage(0);
    } catch (error) {
      toasterMsg(t('BULK_STATUS_FAILED'), 'danger');
    } finally {
      setSaving(false);
    }
  }, [selectedCells, statusIntent, commentDate, statusCommentForSave, t, resetSelection, loadPage]);

  /**
   * Declining the exclusion popup. The selection the user arrived with cannot be bulk-edited
   * as it stands, so this returns to Product Information to pick a different set rather than
   * leaving them on a grid that has silently dropped products. Bulk Edit is only ever reached
   * by a push from that page — a direct hit carries no selection and stops at the
   * "no products selected" state before this popup — so going back always lands there, and
   * matches both the page's own Back button and the Measurements module's cancel.
   */
  const onDeclineExclusion = useCallback((): void => {
    history.goBack();
  }, [history]);

  /** Status segments for the shared approval bar; values are the stored status enum. */
  const approvalOptions = useMemo<ApprovalOption[]>(
    () => [
      { label: t('STATUS_APPROVED'), value: 'approved', variant: 'green' },
      { label: t('STATUS_APPROVED_WITH_COMMENTS'), value: 'approved_with_comments', variant: 'green' },
      { label: t('STATUS_REJECTED'), value: 'rejected', variant: 'red' },
    ],
    [t],
  );

  if (selectedProductIds.length === 0) {
    return <div className="design-bulk-empty">{t('NO_PRODUCTS_SELECTED')}</div>;
  }

  /* Every selected product was excluded: there is no grid to draw, but the popup must still
     name them — otherwise the screen reads as an unexplained empty state. */
  if (eligibleIds !== null && eligibleIds.length === 0) {
    return (
      <>
        <div className="design-bulk-empty">{t('NO_PRODUCTS_WITH_DESIGN_DETAILS')}</div>
        {showExclusionModal && (
          <ProductExclusionModal
            title={t('NO_DESIGN_MODAL_TITLE')}
            message={t('NO_DESIGN_MODAL_MESSAGE')}
            products={excludedProducts}
            confirmLabel={t('NO_DESIGN_MODAL_CONTINUE')}
            onConfirm={() => setShowExclusionModal(false)}
            onClose={onDeclineExclusion}
          />
        )}
      </>
    );
  }

  return (
    /* Dynamic measured height is the documented exception to the no-inline-style rule. */
    <div className="design-bulk-grid" ref={containerRef} style={containerHeight ? { height: containerHeight } : undefined}>
      <DesignBulkAgGrid
        rows={agRows}
        columnCount={columnCount}
        handlers={handlers}
        allSelected={allSelected}
        someSelected={someSelected}
        onToggleAll={onToggleAll}
        loading={loading}
        sortOrder={sortOrder}
        onSortOrderChange={setSortOrder}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        loadingMore={loadingMore}
      />

      {someSelected && (
        <BombiixApprovalBar
          className="bombiix-approval-bar--fixed design-bulk-approval-bar"
          options={approvalOptions}
          value={statusIntent}
          onChange={(value) => onStatusChange(value as DesignStatusValue)}
          showDatePicker
          date={commentDate}
          onDateChange={setCommentDate}
          datePlaceholder={t('SELECT_DATE')}
          showComment
          showCommentCounter
          comment={footerComment}
          onCommentChange={onFooterCommentChange}
          commentMaxLength={200}
          commentPlaceholder={t('ENTER_STATUS_COMMENTS')}
          commentInvalid={commentError}
          onSave={onSave}
          saveDisabled={saving || statusIntent === null}
        />
      )}

      {/* A `--fixed` bar is out of flow, so the grid would otherwise run underneath it and hide
          its last rows. This reserves exactly the bar's height in the flex column instead of
          duplicating that measurement in JS. */}
      {someSelected && <div className="bombiix-approval-bar-spacer" />}

      {(saving || mutating) && (
        <div className="design-bulk-grid__busy" role="status" aria-live="polite" aria-label={t('SAVING')}>
          <Loader />
        </div>
      )}

      {showExclusionModal && (
        <ProductExclusionModal
          title={t('NO_DESIGN_MODAL_TITLE')}
          message={t('NO_DESIGN_MODAL_MESSAGE')}
          products={excludedProducts}
          confirmLabel={t('NO_DESIGN_MODAL_CONTINUE')}
          onConfirm={() => setShowExclusionModal(false)}
          onClose={onDeclineExclusion}
        />
      )}

      {commentTarget && (
        <CanvasCommentPopup
          open
          canvasName={commentTarget.canvas.name}
          initialComment={perCanvasPdfComments[cellKey(commentTarget.devOrderId, commentTarget.canvas.designId)] ?? commentTarget.canvas.pdfExcludedComment ?? ''}
          anchor={commentTarget.anchor}
          onClose={() => setCommentTarget(null)}
          onSave={onSaveComment}
        />
      )}
    </div>
  );
};

export default DesignDetailsBulkGrid;
